import { Component, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LeapStaffRole, OrbitRoleMapItem } from '../../_models/admin/staff-sync.models';
import { StaffSyncService } from '../../_services/staff-sync/staff-sync.service';
import { ROLE_MAP_UNMAPPABLE_CODES } from '../../_constants/staff-sync-roles';
import { DynamicConfirmModalComponent } from '../../shared/dynamic-confirm-modal/dynamic-confirm-modal.component';
import { OrbitRoleMapHistoryData, OrbitRoleMapHistoryDrawerComponent } from './orbit-role-map-history-drawer.component';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';

/** How an empty mapping reads on screen. */
export const NO_TM_ACCESS = 'No TM access';

/**
 * Staff role mapping: which TM staff roles each Orbit role gives — several, one, or none for no TM access.
 * Self-serve so the business can change it without a deploy. A change applies on the next staff sync, so each
 * save is confirmed first. Lives on the Sync tab of New Features, under the staff sync panel.
 *
 * The roles offered are the ones GET api/Maintenance/LeapStaffRoles lists, less ROLE_MAP_UNMAPPABLE_CODES (ADMIN):
 * the sync does not manage those, so a stored ADMIN mapping is shown and dropped on the row's next save.
 */
@Component({
  selector: 'orbit-role-map-editor',
  standalone: true,
  imports: [DatePipe, AdminEmailComponent],
  templateUrl: 'orbit-role-map-editor.component.html'
})
export class OrbitRoleMapEditorComponent implements OnInit {

  public roles: OrbitRoleMapItem[] = [];
  /** Every staff role the API names, for labels: a stored or historic mapping may hold one no longer offered. */
  public leapRoles: LeapStaffRole[] = [];
  /** The roles a mapping may be set to, in the API's order. */
  public mappableRoles: LeapStaffRole[] = [];
  /** The unsaved choice per Orbit role, in mappableRoles order ([] = no TM access). */
  public pending: { [orbitRoleName: string]: string[] } = {};
  public loading = false;
  public saving: string = null;
  public error: string = null;

  constructor(private service: StaffSyncService, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.load();
  }

  public load(): void {
    this.loading = true;
    this.error = null;
    forkJoin({
      roles: this.service.getRoleMap(),
      // The editor still lists the mappings, and allows no TM access, if the role list fails.
      leapRoles: this.service.getLeapStaffRoles().pipe(catchError(() => of([] as LeapStaffRole[])))
    }).subscribe({
      next: ({ roles, leapRoles }) => {
        this.loading = false;
        this.roles = roles || [];
        this.leapRoles = leapRoles || [];
        this.mappableRoles = this.leapRoles.filter(r => this.isMappable(r.code));
        this.pending = {};
      },
      error: error => {
        this.loading = false;
        this.error = (error && error.message) || 'Could not load the role mapping.';
      }
    });
  }

  /** True when Orbit could not be reached, so "new" and "no longer in Orbit" cannot be told. */
  public get orbitUnreachable(): boolean {
    return this.roles.some(r => r.existsInOrbit === null || r.existsInOrbit === undefined);
  }

  public isNewInOrbit(role: OrbitRoleMapItem): boolean {
    return role.existsInOrbit === true && !role.hasMapping;
  }

  public isGoneFromOrbit(role: OrbitRoleMapItem): boolean {
    return role.existsInOrbit === false;
  }

  /** The stored codes: leapRoleCodes, or the comma-separated leapRoleCode when the list is absent. */
  public savedCodes(role: OrbitRoleMapItem): string[] {
    const codes = role.leapRoleCodes || (role.leapRoleCode ? role.leapRoleCode.split(',') : []);
    return codes.map(c => (c || '').trim().toUpperCase()).filter(c => c.length > 0);
  }

  /** Stored codes the editor does not offer (ADMIN): shown, and dropped when the row is saved. */
  public unmappableCodes(role: OrbitRoleMapItem): string[] {
    return this.savedCodes(role).filter(c => !this.isMappable(c));
  }

  /** The ticked codes: the unsaved choice, else the stored mapping less what is not offered. */
  public selected(role: OrbitRoleMapItem): string[] {
    const choice = this.pending[role.orbitRoleName];
    if (choice !== undefined) {
      return choice;
    }
    const saved = this.savedCodes(role);
    return this.mappableRoles.map(r => r.code).filter(c => saved.includes(c));
  }

  public isSelected(role: OrbitRoleMapItem, code: string): boolean {
    return this.selected(role).includes(code);
  }

  public toggle(role: OrbitRoleMapItem, code: string): void {
    const current = this.selected(role);
    const wanted = current.includes(code) ? current.filter(c => c !== code) : [...current, code];
    // Kept in the offered order, so a set always reads the same.
    const ordered = this.mappableRoles.map(r => r.code).filter(c => wanted.includes(c));
    this.pending = { ...this.pending, [role.orbitRoleName]: ordered };
  }

  /** Saveable when the ticked set differs from what is stored, or when a role has no mapping row yet. */
  public isDirty(role: OrbitRoleMapItem): boolean {
    if (!role.hasMapping) {
      return true;
    }
    const saved = this.savedCodes(role);
    const choice = this.selected(role);
    return saved.length !== choice.length || saved.some(c => !choice.includes(c));
  }

  public label(code: string): string {
    const role = this.leapRoles.find(r => r.code === code);
    return role ? role.description + ' (' + role.code + ')' : code;
  }

  /** A set of codes by name, e.g. "Tutor Coordinator Manager (TCMGR), Tutor Coordinator (TTRCRD)". */
  public describe(codes: string[]): string {
    return codes.length === 0 ? NO_TM_ACCESS : codes.map(c => this.label(c)).join(', ');
  }

  /** Ask first — it changes who gets TM access on the next sync — then save and show the stored row. */
  public save(role: OrbitRoleMapItem): void {
    const codes = this.selected(role);
    const message = 'Map Orbit role "' + role.orbitRoleName + '" to ' + this.describe(codes) + '? ' +
      'This changes who gets Tuition Management access on the next staff sync.';

    this.dialog.open(DynamicConfirmModalComponent, {
      width: '440px',
      data: { confirmMessage: message, confirmLabel: 'Save mapping' }
    })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.put(role, codes);
        }
      });
  }

  public history(role: OrbitRoleMapItem): void {
    const data: OrbitRoleMapHistoryData = { orbitRoleName: role.orbitRoleName, leapRoles: this.leapRoles };
    this.dialog.open(OrbitRoleMapHistoryDrawerComponent, {
      data,
      width: '520px',
      height: '100vh',
      maxWidth: '100vw',
      position: { right: '0', top: '0' }
    });
  }

  private isMappable(code: string): boolean {
    return !ROLE_MAP_UNMAPPABLE_CODES.includes((code || '').trim().toUpperCase());
  }

  private put(role: OrbitRoleMapItem, codes: string[]): void {
    this.saving = role.orbitRoleName;
    this.error = null;
    this.service.saveRoleMap(role.orbitRoleName, codes).subscribe({
      next: updated => {
        this.saving = null;
        if (updated) {
          // The saved row carries no Orbit fields; keep the ones the list had, or the page reads as Orbit unreachable.
          const merged: OrbitRoleMapItem = { ...updated, existsInOrbit: role.existsInOrbit, requiredSelectBrand: role.requiredSelectBrand };
          this.roles = this.roles.map(r => r.orbitRoleName === role.orbitRoleName ? merged : r);
        }
        const { [role.orbitRoleName]: _, ...rest } = this.pending;
        this.pending = rest;
      },
      error: error => {
        this.saving = null;
        this.error = 'Could not save the mapping for "' + role.orbitRoleName + '": ' +
          ((error && error.message) || 'please try again.');
      }
    });
  }
}
